import React, { useState, useEffect } from 'react';
import { 
  Library, 
  Save, 
  Plus, 
  Trash2, 
  Edit3, 
  Clock, 
  Upload, 
  Image as ImageIcon, 
  Eye, 
  X,
  List,
  CheckCircle2,
  Info
} from 'lucide-react';
import { useToast } from '../../../components/Toast';
import { EDUCATION_DEFAULTS } from '../../../data/educationDefaults';
import { getImageUrl as resolveMediaSrc } from '../../../utils/mediaUtils';
import './FormsUnified.css';

const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

export default function EducationSubjectsOfferedForm({ apiBase }) {
  const { toast, ToastContainer } = useToast();

  const defaults = EDUCATION_DEFAULTS.subjects_offered || [];

  const [formData, setFormData] = useState({
    kicker: 'Subjects Offered',
    title: 'Balanced Academics, Creativity, and Character Growth',
    subtitle: 'A well-rounded subject mix that strengthens mastery, nurtures curiosity, and shapes students into confident learners.',
    description: 'A well-rounded subject mix that strengthens mastery, nurtures curiosity, and shapes students into confident learners.',
    stats: [
      { label: 'Core Subjects', value: String(Array.isArray(defaults) ? defaults.length : 8) },
      { label: 'Learning Domains', value: '3' },
      { label: 'Cambridge + Local Alignment', value: '100%' }
    ],
    subjects: Array.isArray(defaults) ? [...defaults] : []
  });

  const [originalData, setOriginalData] = useState({ ...formData });
  const [pendingFields, setPendingFields] = useState({});
  const [loading, setLoading] = useState(true);
  const [sectionId, setSectionId] = useState(null);
  const [submitting, setSubmitting] = useState({});
  const [activeSubTab, setActiveSubTab] = useState('overview');
  const [imageUploading, setImageUploading] = useState(false);
  const [imageProgress, setImageProgress] = useState(0);
  const [editingSubjectIndex, setEditingSubjectIndex] = useState(null);
  const [subjectModalData, setSubjectModalData] = useState(null);
  const [newPoint, setNewPoint] = useState('');
  const [mediaMap, setMediaMap] = useState({});
  const [lightboxImage, setLightboxImage] = useState(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('ph_auth_token') || localStorage.getItem('auth_token') || sessionStorage.getItem('token');
    const headers = { 'Content-Type': 'application/json' };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    return headers;
  };

  useEffect(() => {
    const initializeForm = async () => {
      await loadMediaMap();
      await loadSection();
    };
    initializeForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase]);

  const loadMediaMap = async () => {
    try {
      const response = await fetch(`${apiBase}/education/cms/media?_t=${Date.now()}`, { 
        credentials: 'include',
        headers: {
          ...getAuthHeaders(),
          'Cache-Control': 'no-cache'
        }
      });
      if (!response.ok) return;
      const result = await response.json();
      if (result.ok && Array.isArray(result.data)) {
        const map = {};
        result.data.forEach((media) => {
          const id = media.id || media.media_id;
          const url = media.url || media.file_path;
          if (id && url) {
            map[id] = url;
            map[String(id)] = url;
          }
        });
        setMediaMap(map);
      }
    } catch (err) {
      console.error('Media fetch error:', err.message);
    }
  };

  const getMediaUrl = (mediaId) => {
    if (!mediaId) return null;
    if (mediaMap[mediaId]) return mediaMap[mediaId];
    if (mediaMap[String(mediaId)]) return mediaMap[String(mediaId)];
    return resolveMediaSrc(mediaId, mediaMap) || mediaId;
  };

  const loadSection = async () => {
    setLoading(true);
    try {
      let result = null;
      
      const adminResponse = await fetch(`${apiBase}/education/cms/sections`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });
      
      if (adminResponse.ok) {
        result = await adminResponse.json();
      } else if (adminResponse.status === 401) {
        const publicResponse = await fetch(`${apiBase}/education/cms/sections?published=true`, {
          credentials: 'include',
          headers: getAuthHeaders()
        });
        if (publicResponse.ok) {
          result = await publicResponse.json();
        }
      }
      
      if (result && result.ok && Array.isArray(result.data)) {
        let section = result.data.find(s => s.section_type === 'education-subjects-offered-shell');
        if (!section) {
          section = result.data.find(s => s.section_type === 'education-subjects-offered');
        }

        if (section) {
          setSectionId(section.id);
          
          let published = section.content_published;
          if (typeof published === 'string') {
            try { published = JSON.parse(published); } catch (e) { published = {}; }
          }

          const publishedEn = published?.en || published || {};
          const so = (publishedEn.subjects_offered && typeof publishedEn.subjects_offered === 'object' && !Array.isArray(publishedEn.subjects_offered))
            ? publishedEn.subjects_offered
            : {};

          const subjectsList = Array.isArray(publishedEn.subjects)
            ? publishedEn.subjects
            : (Array.isArray(so.subjects)
              ? so.subjects
              : (Array.isArray(publishedEn.subjects_offered)
                ? publishedEn.subjects_offered
                : (Array.isArray(defaults) ? defaults : [])));

          const rawStats = Array.isArray(publishedEn.stats)
            ? publishedEn.stats
            : (Array.isArray(so.stats) ? so.stats : []);

          const initialData = {
            kicker: publishedEn.kicker || so.kicker || 'Subjects Offered',
            title: publishedEn.title || so.title || 'Balanced Academics, Creativity, and Character Growth',
            subtitle: publishedEn.subtitle || so.subtitle || 'A well-rounded subject mix that strengthens mastery, nurtures curiosity, and shapes students into confident learners.',
            description: publishedEn.description || so.description || 'A well-rounded subject mix that strengthens mastery, nurtures curiosity, and shapes students into confident learners.',
            stats: [
              { label: 'Core Subjects', value: String(subjectsList.length) },
              rawStats[1] || { label: 'Learning Domains', value: '3' },
              rawStats[2] || { label: 'Cambridge + Local Alignment', value: '100%' }
            ],
            subjects: subjectsList
          };

          const pendingFieldsMap = {};
          try {
            const pendingResponse = await fetch(
              `${apiBase}/cms/section/${section.id}/with-pending`,
              { credentials: 'include', headers: getAuthHeaders() }
            );
            if (pendingResponse.ok) {
              const pendingResult = await pendingResponse.json();
              if (pendingResult.success && pendingResult.data?.pending_changes) {
                pendingResult.data.pending_changes.forEach(change => {
                  if (change.status === 'pending' && (change.locale === 'en' || !change.locale)) {
                    let val = change.new_value;
                    if (typeof val === 'string') {
                      try { val = JSON.parse(val); } catch (e) {}
                    }

                    if (change.field_id === 'overview' || change.field_id === 'overview.update') {
                      pendingFieldsMap['overview'] = true;
                      if (val && typeof val === 'object') {
                        if (val.kicker) initialData.kicker = val.kicker;
                        if (val.title) initialData.title = val.title;
                        if (val.subtitle) initialData.subtitle = val.subtitle;
                        if (val.description) initialData.description = val.description;
                        if (Array.isArray(val.stats)) initialData.stats = val.stats;
                      }
                    } else if (change.field_id in initialData) {
                      pendingFieldsMap[change.field_id] = true;
                      initialData[change.field_id] = val;
                    } else if (change.field_id && change.field_id.includes('subjects.')) {
                      const match = change.field_id.match(/subjects\.(\d+)\./);
                      if (match) {
                        const sIdx = parseInt(match[1], 10);
                        pendingFieldsMap[`subject_${sIdx}`] = true;
                        if (val && initialData.subjects[sIdx]) {
                          initialData.subjects[sIdx] = val;
                        }
                      }
                    }
                  }
                });
              }
            }
          } catch (err) {
            console.warn('Pending changes check error:', err.message);
          }

          setFormData(initialData);
          setOriginalData(initialData);
          setPendingFields(pendingFieldsMap);
        }
      }
    } catch (err) {
      console.error('Load error:', err);
      toast.error('Error loading subjects form: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveField = async (fieldName, label) => {
    if (!sectionId) {
      toast.error('Section ID not loaded');
      return;
    }

    let valToSend = formData[fieldName];
    if (fieldName === 'stats') {
      valToSend = [
        { label: 'Core Subjects', value: String(formData.subjects.length) },
        ...(formData.stats || []).slice(1)
      ];
    }

    setSubmitting(prev => ({ ...prev, [fieldName]: true }));

    try {
      const response = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          section_id: sectionId,
          field_id: fieldName,
          field_name: label || fieldName.replace(/_/g, ' '),
          old_value: originalData[fieldName],
          new_value: valToSend,
          locale: 'en'
        })
      });

      const result = await response.json();

      if (result.success || response.ok) {
        toast.success(`${label || fieldName} submitted for approval`);
        setPendingFields(prev => ({ ...prev, [fieldName]: true }));
        setOriginalData(prev => ({ ...prev, [fieldName]: valToSend }));
      } else {
        if (response.status === 409) {
          toast.error(`This ${label || fieldName} already has a pending change`);
        } else {
          toast.error(result.error || result.message || 'Submit failed');
        }
      }
    } catch (err) {
      toast.error('Error: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [fieldName]: false }));
    }
  };

  const handleAddSubject = () => {
    const newSub = {
      name: '',
      code: '',
      description: '',
      points: [],
      image: ''
    };
    const nextIdx = formData.subjects.length;
    setFormData(prev => ({
      ...prev,
      subjects: [...prev.subjects, newSub]
    }));
    setEditingSubjectIndex(nextIdx);
    setSubjectModalData(newSub);
    setNewPoint('');
  };

  const handleDeleteSubject = async (index) => {
    if (pendingFields[`subject_${index}`]) {
      toast.info('This subject is awaiting approval');
      return;
    }

    const subToDelete = formData.subjects[index];
    if (!subToDelete) return;

    if (!window.confirm(`Delete subject: ${subToDelete.name || index + 1}?`)) return;

    setSubmitting(prev => ({ ...prev, [`del_sub_${index}`]: true }));
    try {
      const response = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          section_id: sectionId,
          field_id: `subjects.${index}.delete`,
          field_name: `Delete Subject - ${subToDelete.name || index + 1}`,
          old_value: subToDelete,
          new_value: null,
          locale: 'en'
        })
      });

      const result = await response.json();
      if (result.success || response.ok) {
        toast.success(`Subject deletion submitted for approval`);
        setPendingFields(prev => ({ ...prev, [`subject_${index}`]: true }));
        // Update local list
        setFormData(prev => {
          const nextSubs = prev.subjects.filter((_, i) => i !== index);
          return {
            ...prev,
            subjects: nextSubs
          };
        });
      } else {
        toast.error(result.error || 'Delete submission failed');
      }
    } catch (err) {
      toast.error('Error: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [`del_sub_${index}`]: false }));
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_IMAGE_SIZE) {
      toast.error(`Max 5MB, got ${(file.size / 1024 / 1024).toFixed(2)}MB`);
      return;
    }

    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error('Only JPG, PNG, WebP allowed');
      return;
    }

    setImageUploading(true);
    setImageProgress(0);

    try {
      const formDataToUpload = new FormData();
      formDataToUpload.append('file', file);

      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (ev) => {
        if (ev.lengthComputable) {
          const percent = Math.round((ev.loaded / ev.total) * 100);
          setImageProgress(percent);
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status === 200 || xhr.status === 201) {
          try {
            const response = JSON.parse(xhr.responseText);
            if (response.success || response.ok) {
              const mediaId = response.media_id || response.id;
              if (mediaId) {
                setSubjectModalData(prev => ({ ...prev, image: mediaId }));
                toast.success('Subject image uploaded successfully');
              } else {
                toast.error('No media ID returned');
              }
            } else {
              toast.error(response.error || 'Upload failed');
            }
          } catch (parseErr) {
            toast.error('Invalid server response');
          }
        } else {
          toast.error(`Upload failed (HTTP ${xhr.status})`);
        }
        setImageUploading(false);
      });

      xhr.addEventListener('error', () => {
        toast.error('Upload failed - network error');
        setImageUploading(false);
      });

      xhr.open('POST', `${apiBase}/education/cms/media/upload?section_type=education-subjects-offered-shell`);
      xhr.withCredentials = true;
      const token = localStorage.getItem('ph_auth_token') || localStorage.getItem('auth_token') || sessionStorage.getItem('token');
      if (token) {
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      }
      xhr.send(formDataToUpload);
    } catch (err) {
      toast.error('Upload error: ' + err.message);
      setImageUploading(false);
    }
  };

  const handleAddPoint = () => {
    if (!newPoint.trim()) return;
    setSubjectModalData(prev => ({
      ...prev,
      points: [...(prev.points || []), newPoint.trim()]
    }));
    setNewPoint('');
  };

  const handleRemovePoint = (pIdx) => {
    setSubjectModalData(prev => ({
      ...prev,
      points: (prev.points || []).filter((_, i) => i !== pIdx)
    }));
  };

  const handleSubmitSubjectModal = async () => {
    if (!sectionId) {
      toast.error('Section ID not loaded');
      return;
    }

    const subIndex = editingSubjectIndex;
    const sub = subjectModalData;
    const subKey = `subject_${subIndex}`;

    if (!sub || !sub.name?.trim()) {
      toast.warning('Please enter a subject name');
      return;
    }

    const isNew = subIndex >= (originalData.subjects?.length || 0);
    const op = isNew ? 'add' : 'update';
    const fieldId = `subjects.${subIndex}.${op}`;

    setSubmitting(prev => ({ ...prev, [subKey]: true }));

    try {
      const response = await fetch(
        `${apiBase}/cms/field-change/submit`,
        {
          method: 'POST',
          headers: getAuthHeaders(),
          credentials: 'include',
          body: JSON.stringify({
            section_id: sectionId,
            field_id: fieldId,
            field_name: `${isNew ? 'Add' : 'Update'} Subject: ${sub.name}`,
            old_value: isNew ? null : (originalData.subjects[subIndex] || {}),
            new_value: sub,
            locale: 'en'
          })
        }
      );

      const result = await response.json();

      if (result.success || response.ok) {
        toast.success(`Subject "${sub.name}" submitted for approval`);
        setPendingFields(prev => ({ ...prev, [subKey]: true }));
        setFormData(prev => {
          const nextSubs = [...prev.subjects];
          nextSubs[subIndex] = sub;
          return {
            ...prev,
            subjects: nextSubs
          };
        });
        setEditingSubjectIndex(null);
        setSubjectModalData(null);
      } else {
        if (response.status === 409) {
          toast.error('This subject already has a pending approval');
        } else {
          toast.error(result.error || result.message || 'Submit failed');
        }
      }
    } catch (err) {
      toast.error('Error: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [subKey]: false }));
    }
  };

  if (loading) {
    return (
      <div className="unified-loading-container">
        <div className="unified-loading-spinner" />
        <p className="unified-loading-text">Loading Subjects Catalog...</p>
      </div>
    );
  }

  // Calculate secondary stats (indices 1 & 2)
  const secondaryStats = (formData.stats || []).slice(1);

  return (
    <div className="unified-form-container">
      <ToastContainer />

      {/* Header */}
      <div className="unified-header">
        <div className="unified-header-left">
          <div className="unified-icon-badge">
            <Library size={22} />
          </div>
          <div>
            <h2 className="unified-title">Academic Subjects & Disciplines</h2>
            <p className="unified-subtitle">Manage core academic disciplines, specialized learning modules, and curriculum subject catalog</p>
          </div>
        </div>
      </div>

      {/* Sub-Navigation Tabs */}
      <div className="unified-tab-bar" style={{ marginBottom: '24px' }}>
        <button
          type="button"
          className={`tab-pill-btn ${activeSubTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveSubTab('overview')}
        >
          <Library size={15} style={{ marginRight: '6px' }} />
          <span>Overview & Statistics</span>
        </button>

        <button
          type="button"
          className={`tab-pill-btn ${activeSubTab === 'subjects' ? 'active' : ''}`}
          onClick={() => setActiveSubTab('subjects')}
        >
          <List size={15} style={{ marginRight: '6px' }} />
          <span>Subjects Catalog ({formData.subjects.length})</span>
        </button>
      </div>

      {/* TAB 1: OVERVIEW & STATS */}
      {activeSubTab === 'overview' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <h3 style={{ fontSize: '16px', fontWeight: '800', color: '#0f172a', margin: 0 }}>
                Subjects Section Overview
              </h3>
              <p style={{ fontSize: '13px', color: '#64748b', margin: '2px 0 0' }}>
                Introductory text and numerical statistics highlighting our academic breadth
              </p>
            </div>
            {pendingFields['overview'] && (
              <span className="unified-pending-pill"><Clock size={12} /> Pending Review</span>
            )}
          </div>

          {/* Section Tag / Kicker */}
          <div className="unified-field-card">
            <div className="unified-field-header">
              <label className="unified-label">Section Tag / Kicker</label>
              {pendingFields['kicker'] && (
                <span className="unified-pending-pill"><Clock size={12} /> Pending Review</span>
              )}
            </div>
            <div className="unified-input-action-row">
              <input
                type="text"
                maxLength="60"
                className="unified-input"
                value={formData.kicker || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, kicker: e.target.value }))}
                disabled={pendingFields['kicker'] || submitting['kicker']}
                placeholder="e.g. Subjects Offered"
              />
              <button
                type="button"
                className="unified-submit-button"
                onClick={() => handleSaveField('kicker', 'Section Kicker')}
                disabled={submitting['kicker'] || pendingFields['kicker'] || formData.kicker === originalData.kicker}
              >
                <Save size={14} /> {submitting['kicker'] ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>

          {/* Headline Title */}
          <div className="unified-field-card">
            <div className="unified-field-header">
              <label className="unified-label">Headline Title</label>
              {pendingFields['title'] && (
                <span className="unified-pending-pill"><Clock size={12} /> Pending Review</span>
              )}
            </div>
            <div className="unified-input-action-row">
              <input
                type="text"
                maxLength="120"
                className="unified-input"
                value={formData.title || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                disabled={pendingFields['title'] || submitting['title']}
                placeholder="e.g. Balanced Academics, Creativity, and Character Growth"
              />
              <button
                type="button"
                className="unified-submit-button"
                onClick={() => handleSaveField('title', 'Headline Title')}
                disabled={submitting['title'] || pendingFields['title'] || formData.title === originalData.title}
              >
                <Save size={14} /> {submitting['title'] ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>

          {/* Section Subtitle / Tagline */}
          <div className="unified-field-card">
            <div className="unified-field-header">
              <label className="unified-label">Section Subtitle / Tagline</label>
              {pendingFields['subtitle'] && (
                <span className="unified-pending-pill"><Clock size={12} /> Pending Review</span>
              )}
            </div>
            <div className="unified-input-action-row" style={{ alignItems: 'flex-start' }}>
              <textarea
                rows="2"
                maxLength="220"
                className="unified-textarea"
                value={formData.subtitle || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, subtitle: e.target.value }))}
                disabled={pendingFields['subtitle'] || submitting['subtitle']}
                placeholder="e.g. A well-rounded subject mix that strengthens mastery, nurtures curiosity, and shapes students into confident learners."
              />
              <button
                type="button"
                className="unified-submit-button"
                style={{ marginTop: '2px' }}
                onClick={() => handleSaveField('subtitle', 'Section Subtitle')}
                disabled={submitting['subtitle'] || pendingFields['subtitle'] || formData.subtitle === originalData.subtitle}
              >
                <Save size={14} /> {submitting['subtitle'] ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>

          {/* Detailed Description */}
          <div className="unified-field-card">
            <div className="unified-field-header">
              <label className="unified-label">Detailed Description</label>
              {pendingFields['description'] && (
                <span className="unified-pending-pill"><Clock size={12} /> Pending Review</span>
              )}
            </div>
            <div className="unified-input-action-row" style={{ alignItems: 'flex-start' }}>
              <textarea
                rows="3"
                className="unified-textarea"
                value={formData.description || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                disabled={pendingFields['description'] || submitting['description']}
                placeholder="Exposition on how interdisciplinary learning fosters critical thinking and academic excellence..."
              />
              <button
                type="button"
                className="unified-submit-button"
                style={{ marginTop: '2px' }}
                onClick={() => handleSaveField('description', 'Detailed Description')}
                disabled={submitting['description'] || pendingFields['description'] || formData.description === originalData.description}
              >
                <Save size={14} /> {submitting['description'] ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>

          {/* Key Statistics */}
          <div className="unified-field-card">
            <div className="unified-field-header">
              <div>
                <label className="unified-label" style={{ margin: 0 }}>Subject Metrics & Key Indicators</label>
                <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#64748b' }}>
                  Core subjects metric is calculated automatically from the catalog. Secondary indicators can be customized below.
                </p>
              </div>
              {pendingFields['stats'] && (
                <span className="unified-pending-pill"><Clock size={12} /> Pending Review</span>
              )}
            </div>
            
            {/* Automatic Core Subjects Metric Banner */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '14px 18px',
                background: 'linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%)',
                borderRadius: '10px',
                border: '1.5px solid #a7f3d0',
                marginBottom: '16px'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                <div
                  style={{
                    width: '42px',
                    height: '42px',
                    borderRadius: '8px',
                    background: '#dcfce7',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#059669',
                    flexShrink: 0
                  }}
                >
                  <CheckCircle2 size={22} />
                </div>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '14px', fontWeight: '800', color: '#065f46' }}>
                      Core Subjects (Auto-Calculated)
                    </span>
                    <span
                      style={{
                        fontSize: '11px',
                        fontWeight: '700',
                        color: '#047857',
                        background: '#d1fae5',
                        padding: '1px 8px',
                        borderRadius: '12px'
                      }}
                    >
                      Dynamic Sync
                    </span>
                  </div>
                  <p style={{ margin: '3px 0 0', fontSize: '12.5px', color: '#047857' }}>
                    Automatically calculated from the active catalog: <strong>{formData.subjects.length} subjects</strong>. No manual entry needed.
                  </p>
                </div>
              </div>

              <div
                style={{
                  fontSize: '28px',
                  fontWeight: '800',
                  color: '#065f46',
                  padding: '4px 16px',
                  background: '#ffffff',
                  borderRadius: '8px',
                  border: '1px solid #6ee7b7',
                  boxShadow: '0 2px 4px rgba(6,95,70,0.06)'
                }}
              >
                {formData.subjects.length}
              </div>
            </div>

            {/* Additional Secondary Metrics */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: '700', color: '#475569' }}>
                <Info size={14} color="#64748b" /> Additional Key Indicators (Optional)
              </div>
              {secondaryStats.map((stat, idx) => {
                const actualIndex = idx + 1;
                return (
                  <div
                    key={actualIndex}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '12px 16px',
                      background: '#f8fafc',
                      borderRadius: '8px',
                      border: '1px solid #e2e8f0'
                    }}
                  >
                    <div style={{ width: '140px' }}>
                      <label style={{ fontSize: '11px', fontWeight: '800', color: '#64748b', textTransform: 'uppercase' }}>Metric Value</label>
                      <input
                        type="text"
                        className="unified-input"
                        style={{ padding: '6px 10px', fontSize: '13px' }}
                        value={stat.value || ''}
                        onChange={(e) => {
                          const updated = [...formData.stats];
                          updated[actualIndex] = { ...updated[actualIndex], value: e.target.value };
                          setFormData(prev => ({ ...prev, stats: updated }));
                        }}
                        disabled={pendingFields['stats'] || submitting['stats']}
                        placeholder="e.g. 3 or 100%"
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={{ fontSize: '11px', fontWeight: '800', color: '#64748b', textTransform: 'uppercase' }}>Metric Label</label>
                      <input
                        type="text"
                        className="unified-input"
                        style={{ padding: '6px 10px', fontSize: '13px' }}
                        value={stat.label || ''}
                        onChange={(e) => {
                          const updated = [...formData.stats];
                          updated[actualIndex] = { ...updated[actualIndex], label: e.target.value };
                          setFormData(prev => ({ ...prev, stats: updated }));
                        }}
                        disabled={pendingFields['stats'] || submitting['stats']}
                        placeholder="e.g. Learning Domains"
                      />
                    </div>
                  </div>
                );
              })}

              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '6px' }}>
                <button
                  type="button"
                  className="unified-submit-button"
                  onClick={() => handleSaveField('stats', 'Subject Metrics')}
                  disabled={submitting['stats'] || pendingFields['stats'] || JSON.stringify(formData.stats) === JSON.stringify(originalData.stats)}
                >
                  <Save size={14} /> {submitting['stats'] ? 'Saving...' : 'Save Metrics'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* TAB 2: SUBJECTS LIST */}
      {activeSubTab === 'subjects' && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '18px' }}>
            <div>
              <h3 style={{ fontSize: '16px', fontWeight: '800', color: '#0f172a', margin: 0 }}>
                Subject Catalog ({formData.subjects.length})
              </h3>
              <p style={{ fontSize: '13px', color: '#64748b', margin: '2px 0 0' }}>
                Academic subjects, curriculum syllabi, and learning modules. Total count synchronizes automatically to the Core Subjects metric.
              </p>
            </div>
            <button
              type="button"
              className="unified-submit-button"
              onClick={handleAddSubject}
              style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
            >
              <Plus size={16} /> Add Subject
            </button>
          </div>

          {formData.subjects.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 20px', background: '#f8fafc', borderRadius: '14px', border: '2px dashed #cbd5e1' }}>
              <Library size={38} color="#94a3b8" style={{ margin: '0 auto 12px' }} />
              <h4 style={{ fontSize: '15px', color: '#334155', margin: '0 0 4px', fontWeight: '700' }}>No Subjects Added Yet</h4>
              <p style={{ fontSize: '13px', color: '#64748b', margin: 0 }}>Click "Add Subject" above to add to the catalog</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              {formData.subjects.map((sub, idx) => {
                const isPending = pendingFields[`subject_${idx}`];
                const photoUrl = getMediaUrl(sub.image);
                const pointsCount = Array.isArray(sub.points) ? sub.points.length : 0;

                return (
                  <div
                    key={idx}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '20px',
                      padding: '18px 24px',
                      borderRadius: '14px',
                      border: isPending ? '2px solid #f59e0b' : '1px solid #e2e8f0',
                      borderLeft: isPending ? '5px solid #f59e0b' : `5px solid #0d9488`,
                      background: isPending ? '#fffdf7' : '#ffffff',
                      boxShadow: '0 2px 6px rgba(0,0,0,0.03)',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    {/* Thumbnail */}
                    <div
                      style={{
                        width: '90px',
                        height: '70px',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        flexShrink: 0,
                        background: '#0f172a',
                        border: '1px solid #cbd5e1',
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      {photoUrl ? (
                        <img
                          src={photoUrl}
                          alt={sub.name}
                          style={{ width: '100%', height: '100%', objectFit: 'cover', cursor: 'pointer' }}
                          onClick={() => setLightboxImage({ src: photoUrl, title: sub.name })}
                        />
                      ) : (
                        <div style={{ color: '#94a3b8', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                          <ImageIcon size={20} />
                          <span style={{ fontSize: '10px' }}>No photo</span>
                        </div>
                      )}
                      {photoUrl && (
                        <button
                          type="button"
                          onClick={() => setLightboxImage({ src: photoUrl, title: sub.name })}
                          style={{
                            position: 'absolute',
                            bottom: '2px',
                            right: '2px',
                            background: 'rgba(0,0,0,0.65)',
                            color: '#fff',
                            border: 'none',
                            padding: '2px 4px',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center'
                          }}
                          title="Zoom"
                        >
                          <Eye size={10} />
                        </button>
                      )}
                    </div>

                    {/* Info */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
                        <h4 style={{ margin: 0, fontSize: '16px', fontWeight: '800', color: '#0f172a' }}>
                          {sub.name || 'Unnamed Subject'}
                        </h4>
                        {sub.code && (
                          <span style={{ fontSize: '11px', background: '#e6fffa', color: '#0d9488', padding: '2px 8px', borderRadius: '4px', fontWeight: '800' }}>
                            {sub.code}
                          </span>
                        )}
                        {isPending && (
                          <span className="unified-pending-pill" style={{ padding: '2px 8px', fontSize: '10px' }}>
                            <Clock size={10} /> Pending Review
                          </span>
                        )}
                      </div>

                      {sub.description && (
                        <p style={{ margin: '0 0 6px', fontSize: '13px', color: '#64748b', lineHeight: '1.4' }}>
                          {sub.description}
                        </p>
                      )}
                      
                      {pointsCount > 0 ? (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '4px' }}>
                          {sub.points.map((pt, pIdx) => (
                            <span key={pIdx} style={{ fontSize: '11.5px', background: '#f1f5f9', color: '#334155', padding: '1px 8px', borderRadius: '4px' }}>
                              • {pt}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <p style={{ margin: 0, fontSize: '13px', color: '#94a3b8', fontStyle: 'italic' }}>
                          No syllabus points entered yet
                        </p>
                      )}
                    </div>

                    {/* Actions */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                      <button
                        type="button"
                        className="unified-secondary-btn"
                        onClick={() => {
                          if (isPending) {
                            toast.info('This subject is awaiting approval');
                            return;
                          }
                          setEditingSubjectIndex(idx);
                          setSubjectModalData({
                            ...sub,
                            points: Array.isArray(sub.points) ? [...sub.points] : []
                          });
                          setNewPoint('');
                        }}
                        disabled={isPending}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                      >
                        <Edit3 size={14} /> Edit Subject
                      </button>
                      {!isPending && (
                        <button
                          type="button"
                          onClick={() => handleDeleteSubject(idx)}
                          disabled={submitting[`del_sub_${idx}`]}
                          style={{
                            padding: '8px 12px',
                            color: '#ef4444',
                            background: '#fef2f2',
                            border: '1px solid #fecaca',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center'
                          }}
                          title="Delete Subject"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Edit Subject Modal */}
      {editingSubjectIndex !== null && subjectModalData && (
        <div className="unified-modal-overlay" onClick={() => {
          setEditingSubjectIndex(null);
          setSubjectModalData(null);
        }}>
          <div className="unified-modal" onClick={(e) => e.stopPropagation()}>
            <div className="unified-modal-header">
              <div>
                <h3 style={{ margin: 0, fontSize: '17px', fontWeight: '800', color: '#0f172a' }}>
                  {subjectModalData.name ? `Edit Subject: ${subjectModalData.name}` : 'Add Subject Discipline'}
                </h3>
                <p style={{ margin: '2px 0 0', fontSize: '13px', color: '#64748b' }}>
                  Configure subject title, department code, syllabus points, and illustration
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setEditingSubjectIndex(null);
                  setSubjectModalData(null);
                }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}
              >
                <X size={20} />
              </button>
            </div>

            <div className="unified-modal-body">
              {/* Photo Upload Area */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '20px', marginBottom: '20px', padding: '16px', background: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                <div style={{ width: '90px', height: '70px', borderRadius: '8px', overflow: 'hidden', background: '#0f172a', border: '1px solid #cbd5e1', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {subjectModalData.image && getMediaUrl(subjectModalData.image) ? (
                    <img
                      src={getMediaUrl(subjectModalData.image)}
                      alt="Preview"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    <ImageIcon size={24} color="#94a3b8" />
                  )}
                </div>
                <div style={{ flex: 1 }}>
                  <label className="unified-label" style={{ marginBottom: '4px' }}>Subject Illustration</label>
                  <p style={{ margin: '0 0 8px', fontSize: '12px', color: '#64748b' }}>
                    JPG, PNG, WebP • Max 5MB
                  </p>
                  <div style={{ position: 'relative', display: 'inline-block' }}>
                    <input
                      type="file"
                      id="subject-photo-upload"
                      accept="image/jpeg,image/png,image/webp"
                      onChange={handleImageUpload}
                      disabled={imageUploading}
                      style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer' }}
                    />
                    <label htmlFor="subject-photo-upload" className="unified-secondary-btn" style={{ cursor: 'pointer' }}>
                      <Upload size={14} /> {imageUploading ? `Uploading ${imageProgress}%...` : 'Browse & Upload'}
                    </label>
                  </div>
                </div>
              </div>

              {/* Name */}
              <div style={{ marginBottom: '16px' }}>
                <label className="unified-label" style={{ marginBottom: '8px' }}>Subject Name</label>
                <input
                  type="text"
                  className="unified-input"
                  value={subjectModalData.name || ''}
                  onChange={(e) => setSubjectModalData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g. Mathematics & Applied Statistics"
                />
              </div>

              {/* Code */}
              <div style={{ marginBottom: '16px' }}>
                <label className="unified-label" style={{ marginBottom: '8px' }}>Department Code (Optional)</label>
                <input
                  type="text"
                  className="unified-input"
                  value={subjectModalData.code || ''}
                  onChange={(e) => setSubjectModalData(prev => ({ ...prev, code: e.target.value }))}
                  placeholder="e.g. MATH-01 or SCI-CAM"
                />
              </div>

              {/* Description / Summary */}
              <div style={{ marginBottom: '16px' }}>
                <label className="unified-label" style={{ marginBottom: '8px' }}>Subject Overview / Description</label>
                <textarea
                  className="unified-input"
                  style={{ minHeight: '74px', resize: 'vertical' }}
                  value={subjectModalData.description || ''}
                  onChange={(e) => setSubjectModalData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="e.g. Strengthen speaking, reading, and writing through guided literacy..."
                />
              </div>

              {/* Points / Topics */}
              <div style={{ padding: '16px', background: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                <label className="unified-label" style={{ marginBottom: '8px' }}>Core Learning Modules & Topics</label>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '12px' }}>
                  {(subjectModalData.points || []).map((pt, pIdx) => (
                    <div
                      key={pIdx}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '6px 12px',
                        background: '#ffffff',
                        borderRadius: '6px',
                        border: '1px solid #cbd5e1',
                        fontSize: '13px'
                      }}
                    >
                      <span>• {pt}</span>
                      <button
                        type="button"
                        onClick={() => handleRemovePoint(pIdx)}
                        style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '2px' }}
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  ))}
                </div>

                <div style={{ display: 'flex', gap: '8px' }}>
                  <input
                    type="text"
                    className="unified-input"
                    value={newPoint}
                    onChange={(e) => setNewPoint(e.target.value)}
                    placeholder="Add learning topic / module..."
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddPoint(); } }}
                  />
                  <button
                    type="button"
                    className="unified-secondary-btn"
                    onClick={handleAddPoint}
                    disabled={!newPoint.trim()}
                  >
                    <Plus size={14} /> Add
                  </button>
                </div>
              </div>
            </div>

            <div className="unified-modal-footer">
              <button
                type="button"
                className="unified-secondary-btn"
                onClick={() => {
                  setEditingSubjectIndex(null);
                  setSubjectModalData(null);
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="unified-submit-button"
                onClick={handleSubmitSubjectModal}
                disabled={submitting[`subject_${editingSubjectIndex}`] || !subjectModalData.name?.trim() || imageUploading}
              >
                <Save size={14} /> {submitting[`subject_${editingSubjectIndex}`] ? 'Submitting...' : 'Save & Submit Approval'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Lightbox Modal */}
      {lightboxImage && (
        <div className="unified-modal-overlay" onClick={() => setLightboxImage(null)}>
          <div style={{ maxWidth: '90vw', maxHeight: '90vh', position: 'relative' }} onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              onClick={() => setLightboxImage(null)}
              style={{
                position: 'absolute',
                top: '-14px',
                right: '-14px',
                background: '#ffffff',
                border: 'none',
                borderRadius: '50%',
                width: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                cursor: 'pointer'
              }}
            >
              <X size={18} color="#0f172a" />
            </button>
            <img
              src={lightboxImage.src}
              alt={lightboxImage.title || 'Subject photo'}
              style={{ maxWidth: '100%', maxHeight: '85vh', borderRadius: '12px', boxShadow: '0 20px 40px rgba(0,0,0,0.5)' }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
